import { AuditLog } from '@monetize/types/app';
import { formatInteger } from '@monetize/utils/core';
import { formatDate } from 'date-fns/format';
import { getDate } from 'date-fns/getDate';
import { getMonth } from 'date-fns/getMonth';
import { getYear } from 'date-fns/getYear';
import { parseISO } from 'date-fns/parseISO';
import { useEffect, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import { apiGet, handleApiErrorToast } from '../../../../api/axios';
import {
  useDownloadAllAuditLogs,
  useGetAuditLogs,
} from '../../../../api/settingsService';
import {
  MBox,
  MButton,
  MFlex,
  MList,
  MListItem,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
  MText,
} from '../../../../components/Monetize';
import { DateDisplay } from '../../../../components/Monetize/DateDisplay';
import { DatePicker } from '../../../../components/Monetize/DatePicker/DatePicker';
import { useDocumentHead } from '../../../../services/documentHead';
import { pluralize } from '../../../../utils';
import { downloadBlobAsFile } from '../../../../utils/download';

// Styled through `textProps` because `DateDisplay` renders its own `MText`; a
// wrapping `MText` would nest a <p> in a <p>.
const LOG_DATE_TEXT_PROPS = { fontWeight: 'semibold' } as const;

export function AuditLogs() {
  const { setDocTitle } = useDocumentHead();

  const [selectedDate, setSelectedDate] = useState(
    parseISO(new Date().toISOString()),
  );
  const year = getYear(selectedDate);
  const month = getMonth(selectedDate) + 1;
  const day = getDate(selectedDate);
  const [downloadingLogs, setDownloadingLogs] = useState<
    Record<string, boolean>
  >({});

  const { isLoading, data: auditLogData } = useGetAuditLogs(
    {
      year,
      month,
      day,
    },
    { meta: { showErrorToast: true } },
  );

  const { isPending: isDownloadingAll, mutateAsync: getAuditLogsCsvFile } =
    useDownloadAllAuditLogs({ meta: { showErrorToast: true } });

  const downloadLogFile = async (log: AuditLog) => {
    try {
      setDownloadingLogs((prev) => ({ ...prev, [log.key]: true }));

      const apiPath = `api/log/contents/${year}/${month}/${day}/${log.key}`;
      const response = await apiGet(apiPath, { responseType: 'arraybuffer' });
      const filename = `audit-log-${year}-${month}-${day}-${log.key}.csv`;

      downloadBlobAsFile(response.data, filename, 'text/csv');
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      setDownloadingLogs((prev) => ({ ...prev, [log.key]: false }));
    }
  };

  const handleDownloadAll = async () => {
    const result = await getAuditLogsCsvFile({
      year,
      month,
      day,
    });

    downloadBlobAsFile(result.data, result.filename, 'text/csv');
  };

  useEffect(() => {
    setDocTitle('Settings', 'Audit Logs');
  }, [setDocTitle]);

  return (
    <MPageContainer alignItems="stretch" overflowY="auto" overflowX="hidden">
      <MSettingsPageHeader title="Audit Logs">
        <DatePicker
          placeholder="Choose date"
          onChange={(date) => {
            if (date) {
              setSelectedDate(parseISO(date));
            }
          }}
          value={selectedDate}
          clearable
        />
      </MSettingsPageHeader>
      {isLoading && <MPageLoader />}
      {(!auditLogData || auditLogData?.length === 0) && (
        <MText fontSize="lg" textAlign="center">
          No audit logs available for the selected date.
        </MText>
      )}
      <MBox width="60%">
        {auditLogData && auditLogData.length !== 0 && (
          <MFlex mb={4} justifyContent="space-between" alignItems="center">
            <MText>
              Audit Logs for {formatDate(selectedDate, 'MMMM d, yyyy')}
            </MText>
            <MButton
              variant="primary"
              onClick={handleDownloadAll}
              isLoading={isDownloadingAll}
              disabled={isDownloadingAll}
              leftIcon={<MdOutlineFileDownload />}
            >
              Download All
            </MButton>
          </MFlex>
        )}
        <MList variant="plain" gap={4} mb={8}>
          {auditLogData?.map((log) => (
            <MListItem
              key={log.key}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              border={'1px solid'}
              borderColor="tGray.lightPurple"
              borderRadius={4}
              p={4}
            >
              <MBox>
                <DateDisplay
                  date={log.logTimeStamp}
                  timezoneConfigKey="userTimezone"
                  textProps={LOG_DATE_TEXT_PROPS}
                />
                <MText>
                  {formatInteger(log.size)} {pluralize('Byte', log.size)}
                </MText>
              </MBox>
              <MButton
                variant="tertiary"
                size="sm"
                leftIcon={<MdOutlineFileDownload />}
                onClick={() => downloadLogFile(log)}
                isLoading={downloadingLogs[log.key]}
                disabled={isDownloadingAll || downloadingLogs[log.key]}
              >
                Download
              </MButton>
            </MListItem>
          ))}
        </MList>
      </MBox>
    </MPageContainer>
  );
}
